/**
 * Compliance checklist — three buckets, grouped by Act.
 *
 * ── Why three, and why the other two are visible ─────────────────────────────
 *
 * An ordinary checklist shows what applies. This shows what does NOT apply and
 * WHY, and what could not be decided and what would settle it.
 *
 * A compliance the user can see was considered and ruled out — "below the
 * threshold", "wrong entity type" — is a different thing from one that was
 * never checked, and only showing the first lets them tell the difference. And
 * a blank field that silently becomes "does not apply" is how an obligation
 * disappears without anyone noticing, which is worse than getting it wrong,
 * because nobody goes looking for what is not there.
 *
 * ── It never waits for a complete profile ────────────────────────────────────
 *
 * The page renders the moment a company exists, from its type alone. Everything
 * unanswered lands in the third bucket with a link to the field that would
 * settle it. Requiring a full profile before showing anything would mean the
 * feature is invisible exactly when it is most useful.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { FiInfo, FiAlertTriangle, FiChevronRight, FiRefreshCw, FiArrowRight } from 'react-icons/fi';
import AppLayout from '../components/layout/AppLayout';
import PageHeader from '../components/common/PageHeader';
import Button from '../components/common/Button';
import Chip from '../components/common/Chip';
import EmptyState from '../components/common/EmptyState';
import { useNavigate } from 'react-router-dom';
import { complianceChecklistAPI } from '../services/api';
import { useApp } from '../context/AppContext';
import { ROUTES } from '../constants/constants';

const fmtDate = (d) => (d ? new Date(d).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }) : null);

/**
 * Which profile section holds each field, so "fill this in" lands on the box
 * rather than on the company. Anything unmapped falls back to Financial
 * position, which is where the highest-value fields live.
 */
const SECTION_FOR_FIELD = {
  turnover: 'financials',
  paidUpCapital: 'financials',
  authorisedCapital: 'financials',
  borrowings: 'financials',
  publicDeposits: 'financials',
  reservesAndSurplus: 'financials',
  netWorthHistory: 'financials',
  netProfitHistory: 'financials',

  employeeCountHistory: 'workforce',
  esiEligibleCountHistory: 'workforce',

  'establishments[].employeeCount': 'establishments',
  'establishments[].esiEligibleCount': 'establishments',
  'establishments[].state': 'establishments',
  'establishments[].isFactory': 'establishments',
  'establishments[].pollutionCategory': 'establishments',

  nicCode: 'operations',
  sector: 'operations',
  foreignShareholdingPercent: 'operations',
  hasDigitalOperations: 'operations',
  processesPersonalData: 'operations',
  hasEcb: 'operations',
  hasOverseasInvestment: 'operations',

  isHoldingCompany: 'structure',
  isSubsidiaryCompany: 'structure',
  isGovernmentCompany: 'structure',
  governedBySpecialAct: 'structure',
  chairmanIsExecutive: 'structure',
  securitiesInDemat: 'structure',
  hasInternationalTransactions: 'structure',
  hasSpecifiedDomesticTransactions: 'structure',
  isTdsDeductor: 'structure',
  isDpiitStartup: 'structure',
};

const BUCKETS = [
  { key: 'applies', label: 'Applies', tint: '#145f5f', bg: '#e4f5f3', mark: '✓' },
  { key: 'cannotDetermine', label: 'Cannot determine', tint: '#8a6a1f', bg: '#fdf8ee', mark: '?' },
  { key: 'doesNotApply', label: 'Does not apply', tint: '#7a7060', bg: '#f5f2ea', mark: '✗' },
];

function CountTile({ bucket, n, active, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="flex-1 min-w-[150px] text-left rounded-xl border px-3.5 py-3 transition-all"
      style={{
        borderColor: active ? bucket.tint : '#ddd6c6',
        background: active ? bucket.bg : '#fff',
        boxShadow: active ? `inset 0 0 0 1px ${bucket.tint}` : 'none',
      }}
    >
      <div className="text-[26px] font-bold leading-none" style={{ color: bucket.tint }}>{n}</div>
      <div className="mt-1 text-[11.5px] font-semibold text-[#5a5348]">{bucket.label}</div>
    </button>
  );
}

/** One obligation. The reason is always visible — it is the point of the row. */
function ObligationRow({ v, bucket, onFill }) {
  const [open, setOpen] = useState(false);
  const hasDetail = Boolean(v.authority || v.formCode || v.source?.citation || v.penalty != null || v.dueNote);

  return (
    <div style={{ borderTop: '1px solid #e8e3d8' }}>
      <div className="flex items-start gap-2.5 px-3 py-2.5">
        <span className="mt-[2px] w-[14px] shrink-0 text-center text-[12px] font-bold" style={{ color: bucket.tint }}>
          {bucket.mark}
        </span>

        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
            <span className="text-[12.5px] font-semibold text-[#3a352c]">{v.obligation}</span>
            {v.section && <span className="text-[10.5px] text-[#9a9080]">{v.section}</span>}
            {v.formCode && <Chip bg="#f3ebd7" color="#8a6a1f">{v.formCode}</Chip>}
            {/* A verdict resting on a threshold nobody has checked against a
                source should be visibly weaker than one that has. */}
            {v.reviewStatus === 'unverified' && (
              <span className="text-[10px] font-semibold" style={{ color: '#8a6a1f' }} title="This threshold has not been verified against a source">
                unverified threshold
              </span>
            )}
          </div>

          <div className="mt-0.5 text-[11.5px] leading-snug text-[#5a5348]">{v.reason}</div>

          {v.missingFields?.length > 0 && (
            <button type="button" onClick={() => onFill(v.missingFields)}
              className="mt-1 inline-flex items-center gap-1 text-[11px] font-semibold" style={{ color: '#8a6a1f' }}>
              Fill this in <FiArrowRight size={11} />
            </button>
          )}

          {open && hasDetail && (
            <div className="mt-2 rounded-lg px-2.5 py-2 space-y-1" style={{ background: '#faf9f6' }}>
              {v.authority && <Detail k="Authority" v={v.authority} />}
              {v.dueNote && <Detail k="Due date" v={v.dueNote} />}
              {v.penalty != null && <Detail k="Penalty exposure" v={`Rs ${Number(v.penalty).toLocaleString('en-IN')}`} />}
              {v.source?.citation && <Detail k="Source" v={v.source.citation} />}
              <Detail k="Rule" v={`${v.ruleKey} v${v.version}`} />
            </div>
          )}
        </div>

        <div className="shrink-0 text-right">
          {v.dueDate ? (
            <>
              <div className="text-[11.5px] font-semibold text-[#3a352c]">{fmtDate(v.dueDate)}</div>
              {/* A date resting on a year that has not ended is a guess, and
                  must not read the same as a settled one. */}
              <div className="text-[10px]" style={{ color: v.dueBasis === 'estimated' ? '#8a6a1f' : '#9a9080' }}>
                {v.dueBasis === 'estimated' ? 'estimated' : 'due'}
              </div>
            </>
          ) : bucket.key === 'applies' ? (
            <div className="text-[10.5px] text-[#9a9080]">no date</div>
          ) : null}
          {hasDetail && (
            <button type="button" onClick={() => setOpen((o) => !o)}
              className="mt-1 text-[10.5px] font-semibold text-[#7a7060] hover:text-[#145f5f]">
              {open ? 'Less' : 'Details'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

const Detail = ({ k, v }) => (
  <div className="flex gap-2 text-[11px]">
    <span className="w-[110px] shrink-0 text-[#9a9080]">{k}</span>
    <span className="text-[#5a5348]">{v}</span>
  </div>
);

export default function CompliancePage() {
  const { activeCompany } = useApp();
  const navigate = useNavigate();
  const companyId = activeCompany?._id || activeCompany?.id;

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  // Not an error. The feature is rolled out company by company, and a client
  // who is not on the list yet should be told that plainly rather than shown
  // an alarm with a Try again button that will never work.
  const [notEnabled, setNotEnabled] = useState(null);
  const [bucketKey, setBucketKey] = useState('applies');
  const [openActs, setOpenActs] = useState({});

  const load = useCallback(async () => {
    if (!companyId) return;
    setLoading(true);
    setError('');
    setNotEnabled(null);
    try {
      const res = await complianceChecklistAPI.get(companyId);
      setData(res.data);
    } catch (e) {
      if (e?.code === 'APPLICABILITY_DISABLED') {
        setNotEnabled(e?.message || 'Compliance applicability is not enabled for this company yet.');
        setData(null);
        setLoading(false);
        return;
      }
      // Never render an empty checklist on a failure — "nothing applies" and
      // "we could not ask" look identical, and one of them is alarming.
      setError(e?.message || 'Could not build the checklist.');
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [companyId]);

  useEffect(() => { load(); }, [load]);

  const bucket = BUCKETS.find((b) => b.key === bucketKey);
  const rows = useMemo(() => (data ? data[bucketKey] || [] : []), [data, bucketKey]);

  // Only the applicable bucket carries the engine's own ordering — Act, then
  // how often the obligation falls due. The other two are grouped for reading
  // but not ordered, because an ordering there would imply a priority that does
  // not exist.
  const groups = useMemo(() => {
    if (bucketKey === 'applies') return data?.byAct || [];
    const byAct = new Map();
    for (const v of rows) {
      if (!byAct.has(v.act)) byAct.set(v.act, { act: v.act, obligations: [] });
      byAct.get(v.act).obligations.push(v);
    }
    return [...byAct.values()].map((g) => ({ ...g, count: g.obligations.length }));
  }, [bucketKey, rows, data]);

  /**
   * Send the user to the field that would settle this, not merely to the
   * company. Advice you have to go hunting for is advice that gets ignored.
   */
  const goToProfile = (missingFields = []) => {
    const first = Array.isArray(missingFields) ? missingFields[0] : missingFields;
    navigate(ROUTES.COMPANIES, {
      state: {
        editCompanyId: companyId,
        profileSection: SECTION_FOR_FIELD[first] || 'financials',
      },
    });
  };

  if (!companyId) {
    return (
      <AppLayout>
        <div className="p-6">
          <PageHeader title="Compliance" subtitle="Every obligation the rule book has an opinion about." />
          <EmptyState
            title="Select a company"
            description="Choose a company from the sidebar to see what applies to it."
          />
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <div className="p-6">
        <PageHeader
          title="Compliance"
          subtitle={activeCompany?.name || ''}
        />

        {notEnabled ? (
          <div className="rounded-xl border px-4 py-3" style={{ borderColor: '#dcd8ce', background: '#faf9f6' }}>
            <div className="flex items-center gap-2 text-[13px] font-bold" style={{ color: '#5a5348' }}>
              <FiInfo size={14} /> {notEnabled}
            </div>
            <div className="mt-1 text-[12px] text-[#7a7060]">
              It is being switched on company by company. Nothing is wrong with this record.
            </div>
          </div>
        ) : error ? (
          <div className="rounded-xl border px-4 py-3" style={{ borderColor: '#e0c9a0', background: '#fdf8ee' }}>
            <div className="flex items-center gap-2 text-[13px] font-bold" style={{ color: '#8a6a1f' }}>
              <FiAlertTriangle size={14} /> {error}
            </div>
            <div className="mt-1 text-[12px] text-[#5a5348]">
              This is not an empty checklist — nothing could be computed.
            </div>
            <Button variant="ghost" size="sm" className="mt-2" onClick={load} leftIcon={<FiRefreshCw size={13} />}>
              Try again
            </Button>
          </div>
        ) : loading && !data ? (
          <div className="py-12 text-center text-[13px] text-[#7a7060]">Working out what applies…</div>
        ) : !data ? null : (
          <>
            <div className="flex flex-wrap gap-2 mb-2">
              {BUCKETS.map((b) => (
                <CountTile key={b.key} bucket={b} n={data.counts[b.key]}
                  active={b.key === bucketKey} onClick={() => setBucketKey(b.key)} />
              ))}
            </div>

            <div className="mb-4 text-[11px] text-[#7a7060]">
              {data.ruleBook.rulesEvaluated} rules evaluated as at {fmtDate(data.asOf)}.
              {data.counts.cannotDetermine > 0 && bucketKey !== 'cannotDetermine' && (
                <button type="button" onClick={() => setBucketKey('cannotDetermine')}
                  className="ml-1 font-semibold underline underline-offset-2" style={{ color: '#8a6a1f' }}>
                  {data.counts.cannotDetermine} could not be decided
                </button>
              )}
            </div>

            {/* What to fill in, ordered by how much it settles. Counted from the
                real rule book, not a fixed table. */}
            {bucketKey === 'cannotDetermine' && data.resolvers.length > 0 && (
              <div className="rounded-xl border p-3 mb-4" style={{ borderColor: '#e0c9a0', background: '#fdf8ee' }}>
                <div className="text-[12.5px] font-bold mb-1.5" style={{ color: '#8a6a1f' }}>Fill these in first</div>
                <div className="space-y-1">
                  {data.resolvers.slice(0, 6).map((r) => (
                    <button key={r.field} type="button" onClick={() => goToProfile([r.field])}
                      className="w-full flex items-baseline gap-2 text-[11.5px] text-left rounded px-1 -mx-1 hover:bg-[#f3ebd7]">
                      <span className="w-[190px] shrink-0 font-semibold" style={{ color: '#8a6a1f' }}>{r.label}</span>
                      <span className="text-[#5a5348]">
                        settles {r.resolves} — {r.obligations.slice(0, 3).join(', ')}
                        {r.obligations.length > 3 ? `, and ${r.obligations.length - 3} more` : ''}
                      </span>
                    </button>
                  ))}
                </div>
                <Button variant="ghost" size="sm" className="mt-2"
                  onClick={() => goToProfile(data.resolvers.map((r) => r.field))}>
                  Fill in {data.resolvers[0].label}
                </Button>
              </div>
            )}

            {!rows.length ? (
              <EmptyState
                title={bucketKey === 'cannotDetermine' ? 'Everything is decided' : `Nothing in ${bucket.label.toLowerCase()}`}
                description={bucketKey === 'cannotDetermine'
                  ? 'Every rule in the book could be resolved from this company profile.'
                  : 'No obligation fell into this bucket.'}
              />
            ) : (
              <div className="space-y-2.5">
                {groups.map((g) => {
                  const open = openActs[g.act] !== false;
                  return (
                    <div key={g.act} className="rounded-xl border overflow-hidden" style={{ borderColor: '#ddd6c6', background: '#fff' }}>
                      <button type="button"
                        onClick={() => setOpenActs((s) => ({ ...s, [g.act]: !open }))}
                        className="w-full flex items-center gap-2 px-3 py-2.5 text-left"
                        style={{ background: '#f8f5ef' }}>
                        <FiChevronRight size={15} className={`shrink-0 text-[#7a7060] transition-transform ${open ? 'rotate-90' : ''}`} />
                        <span className="text-[13px] font-bold text-[#3a352c]">{g.act}</span>
                        <Chip bg={bucket.bg} color={bucket.tint}>{g.count}</Chip>
                      </button>
                      {open && g.obligations.map((v) => (
                        <ObligationRow key={`${v.ruleKey}-${v.version}`} v={v} bucket={bucket} onFill={goToProfile} />
                      ))}
                    </div>
                  );
                })}
              </div>
            )}
          </>
        )}
      </div>
    </AppLayout>
  );
}
